import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  matchPath,
  useNavigate,
} from "react-router-dom";
import "./App.css";
import { ToastContainer } from "react-toastify";

// Authentication Pages
import Login from "./pages/auth/Login";
import ForgotPassword from "./pages/auth/ForgotPassword";
import VerifyEmail from "./pages/auth/VerifyEmail";
import CreatePassword from "./pages/auth/CreatePassword";
import Register from "./pages/auth/CreateAccount";
import CreateBusiness from "./pages/auth/CreateBusiness";
import CreateBusinessComplete from "./pages/auth/CreateBusinessComplete";

// Main Pages
import HomePage from "./pages/home/Home";
import Orders from "./pages/orders/Orders";
import Payments from "./pages/payment/Payments";
import DashboardMain from "./pages/dashboard/DashboardMain";
import ProfileMain from "./pages/profile/ProfileMain";
import Stock from "./pages/stock/Stock";
import Requests from "./pages/requests/Requests";
import Customers from "./pages/customer/Customers";
import PurchaseLogMain from "./pages/customer/PurchaseLogMain";

// Retail Pages
import RetailHome from "./pages/retailer/pages/home/RetailHome";
import ProductDetails from "./pages/retailer/pages/products/ProductDetails";
import RetailHeader from "./pages/retailer/components/header/RetailHeader";
import Footer from "./components/footer/Footer";
import AllVendors from "./pages/retailer/pages/vendors/AllVendors";
import RetailOrders from "./pages/retailer/pages/orders/Orders";
import RetailCart from "./pages/retailer/pages/cart/Carts";
import Categories from "./pages/retailer/pages/categories/Categories";
import SingleCatList from "./pages/retailer/pages/categories/SingleCatList";
import SingleVendor from "./pages/retailer/pages/vendors/SingleVendor";
import RetailProfile from "./pages/retailer/pages/profile/RetailPofile";
import { Provider } from "react-redux";
import { persistor, store } from "./state/store";
import { PersistGate } from "redux-persist/integration/react";
import LoadingComponent from "./components/loading/Loading";
import RetailRegister from "./pages/retailer/pages/auth/RetailRegister";
import { CartProvider } from "./context/CartContext";
import Success from "./pages/success/Success";
import useAuth from "./state/useAuth";
import ClaimComp from "./pages/claims/ClaimComp";

const authRoutes = [
  "/login",
  "/signup",
  "/forgot-password",
  "/verify-email",
  "/create-password",
  "/create-business",
  "/create-business-address",
  "/retail-signup",
];

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { token } = useAuth();

  useEffect(() => {
    const isAuthPage = authRoutes.some((route) =>
      location.pathname.startsWith(route),
    );
    if (!token && !isAuthPage) {
      navigate("/login");
    }
  }, [token, location.pathname, navigate]);

  // Retail routes that should include Header & Footer
  const retailRoutes = [
    "/retail",
    "/product-details",
    "/all-vendors",
    "/retail-orders",
    "/retail-carts",
    "/retail-categories",
    "/retail-categories-single",
    "/retail-single-vendor/:id",
    "/retail-profile",
  ];

  // Use matchPath to support dynamic routes like /retail-single-vendor/:id
  const isRetailRoute = retailRoutes.some((route) =>
    matchPath({ path: route, end: true }, location.pathname),
  );

  return (
    <>
      <ToastContainer />
      {isRetailRoute && <RetailHeader />}

      <div className="App">
        <Routes>
          {/* Non-Retail Routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/dashboard" element={<DashboardMain />} />
          <Route path="/profile" element={<ProfileMain />} />
          <Route path="/orders" element={<Orders />} />
          <Route path="/payments" element={<Payments />} />
          <Route path="/stocks" element={<Stock />} />
          {/* <Route path="/requests" element={<Requests />} /> */}
          <Route path="/customers" element={<Customers />} />
          <Route path="/purchase-log" element={<PurchaseLogMain />} />
          <Route path="/claims" element={<ClaimComp />} />

          <Route path="/privacy" element={<HomePage />} />

          {/* Authentication Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/create-password" element={<CreatePassword />} />
          <Route path="/signup" element={<Register />} />
          <Route path="/create-business" element={<CreateBusiness />} />
          <Route
            path="/create-business-address"
            element={<CreateBusinessComplete />}
          />

          {/* Retail Routes */}
          <Route path="/retail" element={<RetailHome />} />
          <Route path="/retail-signup" element={<RetailRegister />} />
          <Route path="/product-details" element={<ProductDetails />} />
          <Route path="/all-vendors" element={<AllVendors />} />
          <Route path="/retail-single-vendor/:id" element={<SingleVendor />} />
          <Route path="/retail-orders" element={<RetailOrders />} />
          <Route path="/retail-carts" element={<RetailCart />} />
          <Route path="/retail-categories" element={<Categories />} />
          <Route path="/retail-categories-single" element={<SingleCatList />} />
          <Route path="/retail-profile" element={<RetailProfile />} />
          <Route path="/success" element={<Success />} />
        </Routes>
      </div>

      {isRetailRoute && <Footer />}
    </>
  );
};

function App() {
  return (
    <Provider store={store}>
      <PersistGate loading={<LoadingComponent />} persistor={persistor}>
        <Router>
          <CartProvider>
            <Layout />
          </CartProvider>
        </Router>
      </PersistGate>
    </Provider>
  );
}

export default App;
